import { useCallback, useEffect, useState } from "react";
import { LockKey, Sparkle, Basket, ArrowsClockwise, TrendDown, CreditCard, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export default function PriceLock() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState({ adults: 2, children: 0, budget_per_drop: "", where: "", note: "" });
  const [plan, setPlan] = useState(null);
  const [basket, setBasket] = useState({});
  const [setup, setSetup] = useState({ store: "", cadence: "weekly", term_months: 6, pay: "online", if_short: "swap", deliver_to: "", start_day: "", referral_code: "" });
  const [quote, setQuote] = useState(null);
  const [report, setReport] = useState(null);     // { drop, items: {}, what }
  const [cashout, setCashout] = useState("");
  const [gift, setGift] = useState({ user_id: "", amount: "", reason: "", store: "" });

  const load = useCallback(() => {
    api.get("/lock").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const lines = () => Object.entries(basket)
    .filter(([, q]) => Number(q) > 0)
    .map(([item, qty]) => ({ item, qty: Number(qty) }));

  const askAi = async () => {
    setBusy(true);
    try {
      const { data: out } = await api.post("/lock/plan", {
        adults: Number(ask.adults) || 1, children: Number(ask.children) || 0,
        budget_per_drop: Number(ask.budget_per_drop) || 0,
        where: ask.where, note: ask.note,
      });
      setPlan(out);
      setBasket(Object.fromEntries(out.lines.map((l) => [l.item, l.qty])));
      setSetup((s) => ({ ...s, cadence: out.cadence, term_months: out.term_months,
        store: out.cheapest?.store || s.store }));
      setQuote(out.cheapest ? { stores: out.stores, cheapest: out.cheapest, symbol: out.symbol } : null);
      toast.success("The schedule is worked out");
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const compare = async () => {
    if (lines().length === 0) return toast.error("Put something in the basket first");
    setBusy(true);
    try {
      const { data: out } = await api.post("/lock/compare", {
        lines: lines(), term_months: Number(setup.term_months), where: ask.where,
      });
      setQuote(out);
      if (!setup.store && out.cheapest) setSetup({ ...setup, store: out.cheapest.store });
      toast.success(out.cheapest ? `Cheapest: ${out.cheapest.store}` : "Priced up");
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const lockIt = () => act(() => api.post("/lock/locks", {
    store: setup.store, lines: lines(), cadence: setup.cadence,
    term_months: Number(setup.term_months), pay: setup.pay, if_short: setup.if_short,
    deliver_to: setup.deliver_to, where: ask.where, start_day: setup.start_day,
    referral_code: setup.referral_code,
  }), "Price locked");

  const payDrop = async (d) => {
    try {
      const { data: out } = await api.post("/payments/lock/checkout", {
        drop_id: d.id, origin_url: window.location.origin,
      });
      window.location.href = out.url;
    } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Opening the price lock desk…</p>;

  return (
    <div className="space-y-6" data-testid="price-lock-page">
      <div>
        <p className="overline text-[#0B5C2E]">Price lock</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <LockKey size={30} weight="duotone" className="text-[#0B5C2E]" /> Today's price, held
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="price-lock-explainer">
          Buy the regular shop ahead and hold it at today's price. Tell the assistant who it is
          feeding and it works out the schedule — what, how much, how often and how long to lock
          for — then prices that exact basket at every store so you take the cheapest. If a shelf
          price ever falls below your lock, you pay the lower one, so a lock never costs you more.
        </p>
        {data.saved_total > 0 && (
          <p className="mt-2 text-sm font-semibold text-[#0B5C2E]" data-testid="price-lock-saved">
            Saved so far across your drops: {data.locks[0]?.symbol || "$"}{data.saved_total.toFixed(2)}
          </p>
        )}
      </div>

      {/* ---- The grocery credit card ---- */}
      <section className="border border-[#002FA7] bg-[#EEF3FF] p-4 sm:p-5" data-testid="price-lock-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="overline text-[#002FA7]">
              <CreditCard size={14} weight="bold" className="mr-1 inline" /> Grocery card
            </p>
            <p className="font-display mt-1 text-3xl font-bold text-[#0F172A]"
              data-testid="price-lock-credit-balance">
              {data.locks[0]?.symbol || "$"}{Number(data.credit_balance || 0).toFixed(2)}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Comes off your next drop automatically. Groceries only, and it never runs out of date.
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs text-slate-500">Bring a neighbour in</p>
            <p className="font-display text-base font-bold tracking-widest text-[#002FA7]"
              data-testid="price-lock-referral-code">{data.referral_code}</p>
            <p className="text-xs text-slate-500">
              {data.locks[0]?.symbol || "$"}{Number(data.referral_credit || 0).toFixed(2)} once
              their first drop lands
            </p>
          </div>
        </div>
        {data.credits?.length > 0 && (
          <ul className="mt-3 space-y-1 border-t border-[#C7D6F5] pt-3">
            {data.credits.slice(0, 6).map((c, i) => (
              <li key={c.id} className="flex items-start justify-between gap-3 text-xs"
                data-testid={`price-lock-credit-${i}`}>
                <span className="min-w-0 flex-1 text-slate-600">{c.reason}</span>
                <span className={`shrink-0 font-semibold ${
                  c.status === "available" ? "text-[#0B5C2E]"
                    : c.status === "pending" ? "text-[#B3241B]" : "text-slate-400"
                }`}>
                  {c.symbol}{Number(c.amount).toFixed(2)} · {c.status}
                </span>
              </li>
            ))}
          </ul>
        )}
        {Number(data.credit_balance) > 0 && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs font-semibold text-slate-500" htmlFor="pl-cashout">
              Ask for some of it back
              <Input id="pl-cashout" data-testid="price-lock-cashout-amount" type="number"
                min="1" max={data.credit_balance} className="mt-1 w-28"
                placeholder={Number(data.credit_balance).toFixed(2)}
                value={cashout} onChange={(e) => setCashout(e.target.value)} />
            </label>
            <Button data-testid="price-lock-cashout" size="sm" variant="outline" disabled={busy}
              onClick={() => {
                const amount = Number(cashout || data.credit_balance);
                if (!window.confirm(
                  `Ask for ${data.locks[0]?.symbol || "$"}${amount.toFixed(2)} back? `
                  + "An admin settles a pay-out by hand, so it is slower than spending it on groceries.")) return;
                act(() => api.post("/lock/credits/cashout", { amount, why: "Asked for it back" })
                  .then(() => setCashout("")), "Pay-out requested");
              }}>
              <Gift size={15} className="mr-1.5" /> Ask for it back
            </Button>
          </div>
        )}
        {data.credit_requests?.length > 0 && (
          <div className="mt-3 border-t border-[#C7D6F5] pt-3" data-testid="price-lock-cashout-list">
            <p className="overline text-slate-500">Pay-out requests</p>
            {data.credit_requests.map((r, i) => (
              <div key={r.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs"
                data-testid={`price-lock-cashout-row-${i}`}>
                <span className="min-w-0 flex-1 text-slate-600">
                  {data.can_manage ? `${r.name} — ` : ""}
                  {data.locks[0]?.symbol || "$"}{Number(r.amount).toFixed(2)} · {r.status}
                  {r.why ? ` · ${r.why}` : ""}
                </span>
                {data.can_manage && r.status === "requested" && (
                  <span className="flex shrink-0 gap-2">
                    <Button data-testid={`price-lock-cashout-pay-${i}`} size="sm" disabled={busy}
                      onClick={() => act(() => api.post(`/lock/credits/cashout/${r.id}/settle?paid=true`), "Recorded as paid")}>
                      Mark paid
                    </Button>
                    <Button data-testid={`price-lock-cashout-decline-${i}`} size="sm" variant="outline" disabled={busy}
                      onClick={() => act(() => api.post(`/lock/credits/cashout/${r.id}/settle?paid=false`), "Declined")}>
                      Decline
                    </Button>
                  </span>
                )}
              </div>
            ))}
            {data.can_manage && (
              <p className="mt-2 text-xs text-slate-400">
                A pay-out is settled by hand — marking it paid records it and takes the credit
                off the card, it does not send money by itself.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ---- Goodwill credit ---- */}
      {(data.can_manage || data.to_approve?.length > 0) && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="price-lock-gift-panel">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Put a credit on somebody's card
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            An apology, or a thank you. They are told what it is for.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            <Input data-testid="price-lock-gift-user" placeholder="Customer id" value={gift.user_id}
              onChange={(e) => setGift({ ...gift, user_id: e.target.value })} />
            <Input data-testid="price-lock-gift-amount" type="number" min="1" placeholder="Amount"
              value={gift.amount} onChange={(e) => setGift({ ...gift, amount: e.target.value })} />
            <Input data-testid="price-lock-gift-store" placeholder="Store (yours)" value={gift.store}
              onChange={(e) => setGift({ ...gift, store: e.target.value })} />
            <Input data-testid="price-lock-gift-reason" placeholder="What it is for" value={gift.reason}
              onChange={(e) => setGift({ ...gift, reason: e.target.value })} />
          </div>
          <Button data-testid="price-lock-gift-submit" className="mt-3" disabled={busy}
            onClick={() => act(() => api.post("/lock/credits/issue", {
              user_id: gift.user_id.trim(), amount: Number(gift.amount) || 0,
              reason: gift.reason, store: gift.store.trim(),
            }).then(() => setGift({ user_id: "", amount: "", reason: "", store: "" })),
              "Credit put on their card")}>
            <Gift size={16} className="mr-1.5" /> Add the credit
          </Button>
        </section>
      )}

      {/* ---- Credits waiting on the store ---- */}
      {data.to_approve?.length > 0 && (
        <section data-testid="price-lock-approvals">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Credits waiting on you
          </h2>
          <div className="mt-3 space-y-2">
            {data.to_approve.map((c, i) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white px-4 py-3"
                data-testid={`price-lock-approve-row-${i}`}>
                <span className="min-w-0 flex-1 text-sm text-slate-700">
                  {c.name ? <span className="font-semibold">{c.name}</span> : null}
                  {c.store ? ` · ${c.store}` : ""}
                  {c.drop_reference ? ` · ${c.drop_reference}` : ""}
                  <span className="block text-xs text-slate-500">{c.reason}</span>
                  <span className="font-semibold">{c.symbol}{Number(c.amount).toFixed(2)}</span>
                </span>
                <span className="flex shrink-0 gap-2">
                  <Button data-testid={`price-lock-approve-${i}`} size="sm" disabled={busy}
                    onClick={() => act(() => api.post(`/lock/credits/${c.id}/approve?approve=true`), "Credit approved")}>
                    Approve
                  </Button>
                  <Button data-testid={`price-lock-decline-${i}`} size="sm" variant="outline" disabled={busy}
                    onClick={() => act(() => api.post(`/lock/credits/${c.id}/approve?approve=false`), "Declined")}>
                    Decline
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- AI scheduling ---- */}
      <section className="border border-[#0B5C2E] bg-[#F3FAF5] p-4 sm:p-5" data-testid="price-lock-ai-panel">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
          <Sparkle size={18} weight="duotone" className="text-[#0B5C2E]" /> Let the assistant plan it
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <label className="text-xs font-semibold text-slate-500" htmlFor="pl-adults">
            Adults
            <Input id="pl-adults" data-testid="price-lock-adults" type="number" min="1"
              className="mt-1" value={ask.adults}
              onChange={(e) => setAsk({ ...ask, adults: e.target.value })} />
          </label>
          <label className="text-xs font-semibold text-slate-500" htmlFor="pl-children">
            Children
            <Input id="pl-children" data-testid="price-lock-children" type="number" min="0"
              className="mt-1" value={ask.children}
              onChange={(e) => setAsk({ ...ask, children: e.target.value })} />
          </label>
          <label className="text-xs font-semibold text-slate-500" htmlFor="pl-budget">
            Budget per drop (optional)
            <Input id="pl-budget" data-testid="price-lock-budget" type="number" min="0"
              className="mt-1" value={ask.budget_per_drop}
              onChange={(e) => setAsk({ ...ask, budget_per_drop: e.target.value })} />
          </label>
          <label className="text-xs font-semibold text-slate-500" htmlFor="pl-where">
            Town or city
            <Input id="pl-where" data-testid="price-lock-where" className="mt-1" value={ask.where}
              onChange={(e) => setAsk({ ...ask, where: e.target.value })} />
          </label>
          <Input data-testid="price-lock-note" className="sm:col-span-4"
            placeholder="Anything else — big salad eaters, no meat, baby in the house…"
            value={ask.note} onChange={(e) => setAsk({ ...ask, note: e.target.value })} />
        </div>
        <Button data-testid="price-lock-ask-ai" className="mt-3" disabled={busy} onClick={askAi}>
          <Sparkle size={16} className="mr-1.5" /> Work out my schedule
        </Button>
        {plan && (
          <div className="mt-4 border border-border bg-white p-4" data-testid="price-lock-plan">
            <p className="text-sm leading-relaxed text-slate-700">{plan.line}</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {plan.cadence} · {plan.term_months} month lock
              {plan.best_day ? ` · best day ${plan.best_day}` : ""}
              {plan.cheapest ? ` · cheapest ${plan.cheapest.store}` : ""}
            </p>
            {plan.inflation_note && (
              <p className="mt-2 text-sm text-slate-600">{plan.inflation_note}</p>
            )}
            {plan.skip?.length > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                Left off this drop: {plan.skip.join(" ")}
              </p>
            )}
          </div>
        )}
      </section>

      {/* ---- The basket ---- */}
      <section className="border border-border bg-white p-4 sm:p-5" data-testid="price-lock-basket-panel">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
          <Basket size={18} weight="duotone" className="text-[#002FA7]" /> Your regular basket
        </h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {data.catalogue.map((c) => (
            <div key={c.item} className="flex items-center gap-2 border border-border px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-[#0F172A]">
                {c.item} <span className="text-xs text-slate-400">per {c.pack}</span>
              </span>
              <input type="number" min="0" max="99" value={basket[c.item] ?? ""}
                onChange={(e) => setBasket({ ...basket, [c.item]: e.target.value })}
                data-testid={`price-lock-qty-${c.item.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                aria-label={`${c.item} quantity`}
                className="w-16 border border-border px-2 py-1 text-sm" />
            </div>
          ))}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <select data-testid="price-lock-cadence" aria-label="How often" value={setup.cadence}
            onChange={(e) => setSetup({ ...setup, cadence: e.target.value })}
            className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
            {data.cadences.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="price-lock-term" aria-label="Lock for" value={setup.term_months}
            onChange={(e) => setSetup({ ...setup, term_months: e.target.value })}
            className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
            {data.terms.map((t) => (
              <option key={t} value={t}>Lock {t} months · {data.term_discount[t]}% off</option>
            ))}
          </select>
          <Button data-testid="price-lock-compare" variant="outline" disabled={busy} onClick={compare}>
            <ArrowsClockwise size={16} className="mr-1.5" /> Price every store
          </Button>
        </div>
      </section>

      {/* ---- Store comparison ---- */}
      {quote?.stores?.length > 0 && (
        <section data-testid="price-lock-quote">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            The same basket, every store
          </h2>
          <div className="mt-3 space-y-2">
            {quote.stores.slice(0, 8).map((s, i) => (
              <button key={s.store} type="button" onClick={() => setSetup({ ...setup, store: s.store })}
                data-testid={`price-lock-store-${i}`}
                className={`flex w-full items-center justify-between gap-3 border px-4 py-3 text-left transition-colors duration-150 ${
                  setup.store === s.store ? "border-[#0B5C2E] bg-[#F3FAF5]" : "border-border bg-white hover:bg-slate-50"
                }`}>
                <span className="min-w-0">
                  <span className="block font-semibold text-[#0F172A]">{s.store}</span>
                  <span className="block text-xs text-slate-500">
                    {i === 0 ? "Cheapest for this basket" : `${quote.symbol}${(s.locked_total - quote.stores[0].locked_total).toFixed(2)} more`}
                    {s.missing?.length ? ` · ${s.missing.length} not priced` : ""}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-display text-lg font-bold text-[#0B5C2E]">
                    {quote.symbol}{s.locked_total.toFixed(2)}
                  </span>
                  <span className="block text-xs text-slate-400 line-through">
                    {quote.symbol}{s.today_total.toFixed(2)}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <select data-testid="price-lock-pay" aria-label="How to pay" value={setup.pay}
              onChange={(e) => setSetup({ ...setup, pay: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              <option value="online">Pay online, per drop</option>
              <option value="on_handover">Pay on handover</option>
            </select>
            <select data-testid="price-lock-short" aria-label="If something is short" value={setup.if_short}
              onChange={(e) => setSetup({ ...setup, if_short: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              <option value="swap">Swap for the nearest thing, locked price</option>
              <option value="credit">Skip it and credit me back</option>
            </select>
            <Input data-testid="price-lock-deliver" placeholder="Deliver to" value={setup.deliver_to}
              onChange={(e) => setSetup({ ...setup, deliver_to: e.target.value })} />
            <label className="text-xs font-semibold text-slate-500 sm:col-span-2" htmlFor="pl-start">
              First drop{plan?.best_day ? ` — the assistant suggests a ${plan.best_day}` : ""}
              <Input id="pl-start" data-testid="price-lock-start" type="date" className="mt-1"
                value={setup.start_day}
                onChange={(e) => setSetup({ ...setup, start_day: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-500 sm:col-span-1" htmlFor="pl-referral">
              Neighbour's code (optional)
              <Input id="pl-referral" data-testid="price-lock-referral" className="mt-1"
                value={setup.referral_code}
                onChange={(e) => setSetup({ ...setup, referral_code: e.target.value })} />
            </label>
          </div>
          <Button data-testid="price-lock-submit" className="mt-3" disabled={busy || !setup.store}
            onClick={lockIt}>
            <LockKey size={16} className="mr-1.5" /> Lock it at today's price
          </Button>
        </section>
      )}

      {/* ---- My locks ---- */}
      <section data-testid="price-lock-locks">
        <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">My locks</h2>
        {data.locks.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600" data-testid="price-lock-locks-empty">
            Nothing locked yet.
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {data.locks.map((l, i) => (
              <div key={l.id} className="border border-border bg-white p-4" data-testid={`price-lock-lock-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#0F172A]">
                      {l.store} · {l.symbol}{Number(l.locked_total).toFixed(2)} a drop
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {l.reference} · {l.cadence} · {l.lines.length} items · next {l.next_drop} · until {l.ends_on}
                    </p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {l.status}
                      {l.saved_so_far > 0 ? ` · saved ${l.symbol}${Number(l.saved_so_far).toFixed(2)}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {l.status === "running" && (
                      <>
                        <Button data-testid={`price-lock-skip-${i}`} size="sm" variant="outline" disabled={busy}
                          onClick={() => act(() => api.post(`/lock/locks/${l.id}/skip`), "Next drop skipped")}>
                          Skip next
                        </Button>
                        <Button data-testid={`price-lock-pause-${i}`} size="sm" variant="outline" disabled={busy}
                          onClick={() => act(() => api.post(`/lock/locks/${l.id}/status?status=paused`), "Paused")}>
                          Pause
                        </Button>
                      </>
                    )}
                    {l.status === "paused" && (
                      <Button data-testid={`price-lock-resume-${i}`} size="sm" disabled={busy}
                        onClick={() => act(() => api.post(`/lock/locks/${l.id}/status?status=running`), "Running again")}>
                        Resume
                      </Button>
                    )}
                    {(l.status === "running" || l.status === "paused") && (
                      <Button data-testid={`price-lock-cancel-${i}`} size="sm" variant="outline" disabled={busy}
                        onClick={() => act(() => api.post(`/lock/locks/${l.id}/status?status=cancelled`), "Cancelled")}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
                <ul className="mt-2 space-y-1">
                  {l.lines.map((line, li) => (
                    <li key={line.item} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="min-w-0 flex-1">
                        {line.item} — locked {l.symbol}{Number(line.locked_price).toFixed(2)}
                        {line.price_when_locked > line.locked_price
                          ? ` (shelf was ${l.symbol}${Number(line.price_when_locked).toFixed(2)})` : ""}
                      </span>
                      {l.status === "running" || l.status === "paused" ? (
                        <input type="number" min="1" max="99" key={`${line.item}-${line.qty}`}
                          defaultValue={line.qty}
                          aria-label={`${line.item} quantity`}
                          data-testid={`price-lock-lineqty-${i}-${li}`}
                          onBlur={(e) => {
                            const next = Number(e.target.value);
                            if (!e.target.value || next === line.qty) {
                              e.target.value = line.qty;
                              return;
                            }
                            act(() => api.post(`/lock/locks/${l.id}/quantity`,
                              { item: line.item, qty: next }), "Amount changed");
                          }}
                          className="w-14 shrink-0 border border-border px-2 py-1 text-xs" />
                      ) : (
                        <span className="shrink-0">× {line.qty}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-400">
                  {l.pay === "online" ? "Paying online, per drop" : "Paying on handover"} ·
                  {l.if_short === "swap" ? " short items swapped at your locked price" : " short items credited back"}
                  · change an amount by typing over it
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- Drops ---- */}
      {data.drops.length > 0 && (
        <section data-testid="price-lock-drops">
          <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
            <TrendDown size={18} weight="duotone" className="text-[#0B5C2E]" /> Drops
          </h2>
          <div className="mt-3 space-y-3">
            {data.drops.map((d, i) => (
              <div key={d.id} className="border border-border bg-white p-4" data-testid={`price-lock-drop-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#0F172A]">
                      {d.day} · {d.store} · {d.symbol}{Number(d.total).toFixed(2)}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {d.reference} · {d.lines.length} items · shelf today {d.symbol}
                      {Number(d.live_total).toFixed(2)} · saved {d.symbol}{Number(d.saved).toFixed(2)}
                      {d.credit_used > 0 ? ` · ${d.symbol}${Number(d.credit_used).toFixed(2)} off the card` : ""}
                      {d.swaps?.length ? ` · ${d.swaps.length} swapped` : ""}
                      {d.credits?.length ? ` · ${d.credits.length} credited back` : ""}
                    </p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {d.status} · {d.payment_status}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {d.pay === "online" && d.payment_status === "unpaid" && (
                      <Button data-testid={`price-lock-pay-${i}`} size="sm" onClick={() => payDrop(d)}>
                        Pay {d.symbol}{Number(d.total).toFixed(2)}
                      </Button>
                    )}
                    {d.status === "due" && (
                      <Button data-testid={`price-lock-received-${i}`} size="sm" variant="outline" disabled={busy}
                        onClick={() => act(() => api.post(`/lock/drops/${d.id}/received`), "Marked as received")}>
                        Got it
                      </Button>
                    )}
                    <Button data-testid={`price-lock-report-${i}`} size="sm" variant="outline"
                      disabled={busy || d.reported}
                      onClick={() => setReport({ drop: d, items: {}, what: "" })}>
                      {d.reported ? "Reported" : "Report a problem"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* ---- Report a problem ---- */}
      {report && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          data-testid="price-lock-report-dialog" onClick={() => setReport(null)}>
          <div className="w-full max-w-md border border-border bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
              What was wrong with {report.drop.day}'s drop?
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Tick what turned up spoiled or wrong. {report.drop.store} settles the credit.
            </p>
            <div className="mt-3 max-h-56 space-y-1 overflow-y-auto">
              {report.drop.lines.map((l, li) => (
                <label key={l.item} className="flex items-center gap-2 border border-border px-3 py-2 text-sm">
                  <input type="checkbox" className="h-4 w-4"
                    data-testid={`price-lock-report-item-${li}`}
                    checked={!!report.items[l.item]}
                    onChange={(e) => setReport({
                      ...report, items: { ...report.items, [l.item]: e.target.checked },
                    })} />
                  <span className="min-w-0 flex-1 truncate text-[#0F172A]">{l.item}</span>
                  <span className="shrink-0 text-xs text-slate-500">
                    {report.drop.symbol}{Number(l.charged).toFixed(2)}
                  </span>
                </label>
              ))}
            </div>
            <Input data-testid="price-lock-report-what" className="mt-3"
              placeholder="What was wrong with it?" value={report.what}
              onChange={(e) => setReport({ ...report, what: e.target.value })} />
            <div className="mt-3 flex gap-2">
              <Button data-testid="price-lock-report-submit" disabled={busy}
                onClick={() => {
                  const items = Object.entries(report.items).filter(([, v]) => v).map(([k]) => k);
                  if (items.length === 0) return toast.error("Tick what was wrong first");
                  const id = report.drop.id;
                  const what = report.what;
                  setReport(null);
                  act(() => api.post(`/lock/drops/${id}/report`, { items, what_happened: what }),
                    "Reported — the store settles the credit");
                }}>
                Send it
              </Button>
              <Button data-testid="price-lock-report-close" variant="outline"
                onClick={() => setReport(null)}>
                Not now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
